/**
 * Plays the Demirix mark into the browser tab as a short reveal, resting on the static
 * icon in between, and repeating on a fixed cycle.
 *
 * Design notes, because the obvious implementations are all worse:
 *
 *  - It runs in bursts, not continuously. The source artwork animates from nothing to
 *    the finished mark, so looping it end-to-end makes the tab icon throb between a
 *    ghost and a solid logo. Instead: three 900ms pulses spaced 4500ms apart, then 30s
 *    of complete quiet, then the burst repeats. Across a full 39.9s cycle the icon is in
 *    motion about 7% of the time and shows the ordinary static brand mark for the rest.
 *    The quiet stretch is anchored to a wall-clock deadline, so backgrounding the tab
 *    and returning resumes the remainder of the rest instead of restarting the burst,
 *    without that, ordinary tab switching kept the duty cycle far higher than 7%.
 *
 *    ACCESSIBILITY NOTE, deliberately recorded: a burst spans 9.9s from the first pulse
 *    to the end of the third. WCAG 2.2 SC 2.2.2 (Pause, Stop, Hide, Level A) applies to
 *    automatically-moving content that runs longer than five seconds and is presented
 *    alongside other content, and it requires a mechanism to pause, stop or hide it.
 *
 *    Being precise about why we do not meet it: the criterion does NOT require the
 *    control to sit next to the moving thing, a control anywhere on the page would
 *    satisfy it. We have simply chosen not to add site UI to pause a decorative tab
 *    icon. That is a product decision, not an impossibility, and it should be described
 *    that way rather than as a technical constraint.
 *
 *    prefers-reduced-motion is honoured and suppresses the animation completely, which
 *    covers users who have expressed the preference at OS level. It is not itself the
 *    "mechanism" SC 2.2.2 asks for, so this likely does not conform for a user who has
 *    not set it. Options if that is ever revisited: expose a toggle in site settings, or
 *    shorten the burst below five seconds (three pulses need PULSE_INTERVAL_MS <= 2050).
 *
 *  - Every pulse ENDS on the real icon file, not on the last canvas frame. The resting
 *    state is therefore a normal cacheable URL rather than a data: URL, which keeps the
 *    favicon URL stable for crawlers and for the prerender step.
 *
 *    The last DRAWN frame is not the resting image: at progress 5/6 the mark is still
 *    ~4% into its overshoot, so pointing the link back at the file is the final beat of
 *    the animation settling, not a no-op. stateAt(1) does equal the static artwork
 *    exactly, but that state is never rasterised, the file supplies it.
 *
 *  - Frames are drawn with Path2D into an offscreen canvas rather than fetched as
 *    pre-rendered PNGs. Zero extra network requests, zero binary frames in the repo, and
 *    the mark colour is chosen per frame, which is how dark mode is handled here rather
 *    than relying on media= on link[rel=icon] (still unimplemented in Firefox, Bugzilla
 *    1603885).
 *
 *  - Hidden tabs settle and stop. Background timers are throttled to roughly once per
 *    second and then once per minute, so a loop left running would crawl and could
 *    strand the icon on an arbitrary mid-reveal frame, the worst possible resting
 *    state. On visibilitychange we cancel and pin the correct static icon instead.
 *
 *  - Nothing starts until the page has finished loading. While a document is loading the
 *    browser shows a progress indicator in the tab INSTEAD of the favicon, so any frames
 *    drawn before the load event are invisible, and on a slow connection that was most
 *    of the first pulse. Measured on a throttled connection: the module executed at
 *    readyState "interactive", ~11.5s before the page settled.
 *
 * Safari does not re-read the favicon after initial load (WebKit 266426), so there the
 * static icon from index.html simply stays. That is the intended fallback, not a bug.
 *
 * ---------------------------------------------------------------------------------
 * Safari: what has already been tried, so nobody spends another day on it
 *
 * Two things are commonly asked for and neither is achievable: making the tab icon
 * animate in Safari, and giving Safari a black-in-light / white-in-dark icon. Both were
 * investigated against real Safari 26.5.2 on macOS (2026-08-24), by photographing the
 * actual tab strip rather than trusting DOM state. Every mechanism below FAILED:
 *
 *   Animation
 *     - assigning link.href (what this module does)  -> ignored
 *     - removing the <link> and inserting a fresh one -> ignored
 *     - data: and blob: URL frames                    -> ignored
 *     - SVG favicon with SMIL animation               -> renders one static frame
 *     - animated GIF / APNG as the icon file          -> REJECTED; Safari falls back
 *                                                        to a letter placeholder
 *   Colour scheme
 *     - media="(prefers-color-scheme: dark)" on the link -> ignored
 *     - @media (prefers-color-scheme) inside an SVG icon -> ignored
 *     - fill="CanvasText" (CSS system colour)            -> resolves light
 *     - light-dark(#000, #fff)                           -> resolves light
 *     - server-side Sec-CH-Prefers-Color-Scheme hint     -> Safari sends no hint
 *
 * Root cause for the first group: WebKit collects the document's icons exactly once,
 * from Document::implicitClose(), and a FIXME there records that reacting to later
 * changes was a deliberate non-choice. Root cause for the second: Safari rasterises
 * favicons in a fixed light context, so no way of asking "which scheme?" returns dark.
 *
 * Consequence, and it is fine: Safari shows the declared light (black) icon, and draws
 * its own light backing plate behind it so it stays legible on dark chrome. The plate is
 * Safari's, not ours. Our PNGs are fully transparent. It cannot be removed while the
 * icon is dark, because the plate exists precisely to stop a dark icon disappearing.
 * ---------------------------------------------------------------------------------
 *
 * ---------------------------------------------------------------------------------
 * Why one icon link
 *
 * index.html must declare EXACTLY ONE <link rel="icon">, and it must carry no `sizes`
 * attribute. Both are load-bearing, and getting either wrong disables this module
 * silently: the code still runs and still assigns hrefs, the browser just ignores them.
 *
 * Chromium scores a sizeless rel="icon" candidate 1.0, the maximum, and only downloads
 * a later candidate whose score is strictly higher (components/favicon/core/
 * favicon_handler.cc, FaviconCandidate::FromFaviconURL). A sizeless icon therefore beats
 * one carrying sizes="32x32" regardless of document order. When a second icon link was
 * present, 29 href updates produced 29 refetches of that other icon and zero fetches of
 * ours, and the tab changed on 0 of 39 captured screen frames.
 *
 * public/favicon.ico still ships and is still picked up from the site root without a
 * link element, so dropping its <link> costs nothing.
 * ---------------------------------------------------------------------------------
 */
import mark from '../assets/favicon/mark.json'

/** Length of one reveal. */
const REVEAL_MS = 900
/** Time between the start of one pulse and the next, within a burst. */
const PULSE_INTERVAL_MS = 4500
/** How many pulses make up one burst. */
const PULSES_PER_BURST = 3
/** Quiet time after a burst finishes, before the next burst begins. */
const BURST_REST_MS = 30000
/** Both elements enter at this fraction of their final size. */
const START_SCALE = 0.55
/** The solid letterform finishes its move this far through the reveal. */
const SOLID_PORTION = 0.55
/** The crescent starts entering this far through the reveal. */
const CRESCENT_START = 0.35
/** How far the crescent slides in from, in source-artboard units. */
const SLIDE_FROM_SOURCE_UNITS = 55
/** Canvas size for the generated frames: covers a 32px tab slot at 2x DPR. */
const CANVAS_PX = 64
/**
 * Gap between rendered frames.
 *
 * This is not a stylistic choice. Measured on real Chromium by photographing the tab
 * strip: the browser repaints a favicon roughly 5 times per second no matter how often
 * the href is assigned. Driving this from requestAnimationFrame produced ~30 canvas
 * draws and PNG encodes per reveal of which only ~3 were ever painted, ~90% waste, and
 * motion that skipped most of its intermediate states anyway.
 *
 * So render at the rate the browser can actually show, and make every frame count.
 */
const FRAME_INTERVAL_MS = 150
/** Frames drawn per reveal. The final state is the resting file, not a drawn frame. */
const FRAME_COUNT = Math.max(2, Math.round(REVEAL_MS / FRAME_INTERVAL_MS))

const LIGHT_FG = '#000000'
const DARK_FG = '#FFFFFF'

const DARK_QUERY = '(prefers-color-scheme: dark)'
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'

/**
 * easeOutBack, overshoots then settles to exactly 1.
 * Peak overshoot of the ease is (4/27) * c1^3 / (c1+1)^2. That gets multiplied by
 * (1 - START_SCALE) when mapped onto scale, so c1 is tuned so the *visible* overshoot
 * is ~7% (about 1px at a 16px tab icon) rather than the ~2% a gentler curve would give.
 */
const BACK_C1 = 2.2
const BACK_C3 = BACK_C1 + 1
const easeOutBack = (t: number): number =>
  1 + BACK_C3 * Math.pow(t - 1, 3) + BACK_C1 * Math.pow(t - 1, 2)

const clamp01 = (n: number): number => (n < 0 ? 0 : n > 1 ? 1 : n)

interface ElementState {
  scale: number
  opacity: number
  translateX: number
}

/** The scale baked into mark.json when the artboard was fitted to the square viewBox. */
const MARK_SCALE =
  (mark.viewBox * mark.safeArea) /
  Math.max(mark.sourceArtboard.width, mark.sourceArtboard.height)
const SLIDE_FROM = SLIDE_FROM_SOURCE_UNITS * MARK_SCALE

/** Position of both elements at `progress` through one reveal (0..1). */
export function stateAt(progress: number): { solid: ElementState; crescent: ElementState } {
  const tSolid = clamp01(progress / SOLID_PORTION)
  const tCrescent = clamp01((progress - CRESCENT_START) / (1 - CRESCENT_START))
  const easedSolid = easeOutBack(tSolid)
  const easedCrescent = easeOutBack(tCrescent)

  return {
    solid: {
      // Full opacity throughout: an opacity fade is illegible at 16px, so only the
      // scale animates. This is why the first frame is already crisp.
      scale: START_SCALE + (1 - START_SCALE) * easedSolid,
      opacity: 1,
      translateX: 0,
    },
    crescent: {
      scale: START_SCALE + (1 - START_SCALE) * easedCrescent,
      // A one-to-two frame fade so it joins rather than appearing abruptly.
      opacity: tCrescent <= 0 ? 0 : Math.min(1, tCrescent / 0.15),
      translateX: (1 - easedCrescent) * SLIDE_FROM,
    },
  }
}

type Paths = { name: string; path: Path2D; anchor: number[] }[]

function buildPaths(): Paths {
  return mark.elements.map((element) => ({
    name: element.name,
    path: new Path2D(element.d),
    anchor: element.anchor,
  }))
}

function drawFrame(
  ctx: CanvasRenderingContext2D,
  paths: Paths,
  progress: number,
  colour: string
): void {
  const { viewBox, fillRule } = mark
  ctx.clearRect(0, 0, CANVAS_PX, CANVAS_PX)
  ctx.save()
  ctx.scale(CANVAS_PX / viewBox, CANVAS_PX / viewBox)
  ctx.fillStyle = colour

  const state = stateAt(progress)
  for (const { name, path, anchor } of paths) {
    const s = name === 'solid' ? state.solid : state.crescent
    const [ax, ay] = anchor

    ctx.save()
    ctx.globalAlpha = s.opacity
    ctx.translate(s.translateX, 0)
    ctx.translate(ax, ay)
    ctx.scale(s.scale, s.scale)
    ctx.translate(-ax, -ay)
    ctx.fill(path, fillRule as CanvasFillRule)
    ctx.restore()
  }

  ctx.restore()
}

/**
 * Starts the looping reveal. Safe to call once at startup; returns a disposer that
 * cancels any pending frame or cycle and detaches listeners.
 */
export function startAnimatedFavicon(): () => void {
  const noop = () => {}
  if (typeof window === 'undefined' || typeof document === 'undefined') return noop

  const link = document.querySelector<HTMLLinkElement>('link#dynamic-favicon')
  // No element to drive means the HTML changed without this module being updated.
  // Leave whatever icon the document declares rather than inventing a new <link>.
  if (!link) return noop

  // Resting URLs live in index.html so there is one place to bump the cache version.
  const restingLight = link.dataset.iconLight || link.href
  const restingDark = link.dataset.iconDark || restingLight

  const prefersDark = window.matchMedia?.(DARK_QUERY)
  const prefersReduced = window.matchMedia?.(REDUCED_MOTION_QUERY)

  const settle = (): void => {
    link.href = prefersDark?.matches ? restingDark : restingLight
  }

  let frameTimer: ReturnType<typeof setTimeout> | undefined
  let cycleTimer: ReturnType<typeof setTimeout> | undefined
  let pulsesInBurst = 0
  // Wall-clock deadline of the current quiet stretch. Without this the rest is
  // indistinguishable from "start of a fresh burst", so returning to a backgrounded tab
  // re-armed a pulse 3.6s later and the 30s rest never actually happened.
  let restUntil = 0
  let disposed = false

  const cancelPending = (): void => {
    if (frameTimer !== undefined) clearTimeout(frameTimer)
    frameTimer = undefined
    if (cycleTimer !== undefined) clearTimeout(cycleTimer)
    cycleTimer = undefined
  }

  const onSchemeChange = () => settle()

  const detach = (): void => {
    prefersDark?.removeEventListener?.('change', onSchemeChange)
    prefersReduced?.removeEventListener?.('change', onReducedChange)
    document.removeEventListener('visibilitychange', onVisibilityChange)
    window.removeEventListener('load', onWindowLoad)
  }

  const stop = (): void => {
    if (disposed) return
    disposed = true
    cancelPending()
    detach()
  }

  let ctx: CanvasRenderingContext2D | null = null
  let paths: Paths = []
  let canvas: HTMLCanvasElement | null = null

  const canAnimate = (): boolean =>
    !disposed &&
    !!ctx &&
    paths.length > 0 &&
    !prefersReduced?.matches &&
    !document.hidden

  /**
   * Runs one reveal as FRAME_COUNT evenly-spaced states, then rests on the real icon
   * file and schedules the next cycle.
   *
   * Frames are stepped on a fixed timer rather than requestAnimationFrame: rAF fires at
   * the display refresh rate, which is ~20x faster than the browser will repaint a
   * favicon, so it only burns CPU encoding frames nobody ever sees.
   */
  const runReveal = (): void => {
    // Never let two frame chains run at once: a visibility or preference change landing
    // mid-pulse would otherwise interleave frames and double-count the burst.
    if (frameTimer !== undefined) return
    if (!canAnimate()) {
      settle()
      return
    }
    restUntil = 0
    const context = ctx as CanvasRenderingContext2D
    let step = 0

    const drawStep = (): void => {
      if (disposed) return
      // Losing visibility mid-reveal must not freeze the icon on a partial frame.
      if (document.hidden || prefersReduced?.matches) {
        cancelPending()
        settle()
        return
      }

      // progress runs 0 .. (FRAME_COUNT-1)/FRAME_COUNT; progress 1 is the resting file,
      // which is pixel-identical to the final drawn state, so the handoff is invisible.
      const progress = step / FRAME_COUNT

      try {
        drawFrame(context, paths, progress, prefersDark?.matches ? DARK_FG : LIGHT_FG)
        link.href = canvas!.toDataURL('image/png')
      } catch {
        // A tainted canvas or a failed encode should never leave a broken icon.
        settle()
        stop()
        return
      }

      step += 1
      if (step < FRAME_COUNT) {
        frameTimer = setTimeout(drawStep, FRAME_INTERVAL_MS)
        return
      }

      // Hold the last drawn frame for its full slot, then land on the real file.
      frameTimer = setTimeout(() => {
        frameTimer = undefined
        // Only a pulse that actually ran to the end counts against the budget; one cut
        // short by the tab being hidden was never seen, so it should not be spent.
        pulsesInBurst += 1
        settle()
        scheduleNextCycle()
      }, FRAME_INTERVAL_MS)
    }

    drawStep()
  }

  const scheduleNextCycle = (): void => {
    if (disposed) return
    if (cycleTimer !== undefined) clearTimeout(cycleTimer)
    cycleTimer = undefined

    if (pulsesInBurst >= PULSES_PER_BURST) {
      // Burst finished: go quiet, and record when the quiet ends so an interruption can
      // resume the remainder rather than restarting the clock.
      pulsesInBurst = 0
      restUntil = Date.now() + BURST_REST_MS
      cycleTimer = setTimeout(runReveal, BURST_REST_MS)
      return
    }

    // Resuming inside a quiet stretch: serve out what is left of it.
    const remainingRest = restUntil - Date.now()
    if (remainingRest > 0) {
      cycleTimer = setTimeout(runReveal, remainingRest)
      return
    }

    // Mid-burst: the next pulse lands PULSE_INTERVAL_MS after this one started.
    cycleTimer = setTimeout(runReveal, Math.max(0, PULSE_INTERVAL_MS - REVEAL_MS))
  }

  /**
   * Start, once the page has finished loading. While a document loads the browser shows
   * a progress indicator in the tab instead of the favicon, so frames drawn before the
   * load event are never seen by anyone.
   */
  function begin(): void {
    if (disposed) return
    if (document.readyState === 'complete') {
      scheduleFirstPulse()
      return
    }
    window.addEventListener('load', onWindowLoad, { once: true })
  }

  /** First pulse of a session runs immediately; later ones go through the scheduler. */
  function scheduleFirstPulse(): void {
    runReveal()
  }

  function onReducedChange(): void {
    if (prefersReduced?.matches) {
      cancelPending()
      settle()
    } else if (!disposed) {
      // Preference turned back off: resume with a fresh burst rather than mid-sequence,
      // and go through begin() so the load gate is still respected.
      pulsesInBurst = 0
      restUntil = 0
      begin()
    }
  }

  function onWindowLoad(): void {
    if (disposed) return
    scheduleFirstPulse()
  }

  function onVisibilityChange(): void {
    if (disposed) return
    if (document.hidden) {
      // Pin the correct static icon rather than leaving a half-drawn frame behind.
      cancelPending()
      settle()
    } else {
      scheduleNextCycle()
    }
  }

  // Keep the resting icon correct if the OS theme flips later in the session.
  prefersDark?.addEventListener?.('change', onSchemeChange)
  // Honouring prefers-reduced-motion is not optional: the animation auto-starts and
  // repeats indefinitely (WCAG 2.2 SC 2.2.2, Level A).
  prefersReduced?.addEventListener?.('change', onReducedChange)
  document.addEventListener('visibilitychange', onVisibilityChange)

  // Feature-detect Path2D before touching the canvas at all. Environments without 2D
  // canvas (jsdom, some hardened embeddings) throw from getContext rather than
  // returning null, and probing it first would emit a spurious error either way.
  //
  // This happens BEFORE the reduced-motion check on purpose. Returning early without a
  // context left ctx null forever, so a user who loaded the page with reduced motion on
  // and later turned it off got nothing: onReducedChange would schedule a reveal that
  // canAnimate() then rejected. Build the context first, decide whether to run second.
  if (typeof Path2D === 'function') {
    try {
      canvas = document.createElement('canvas')
      canvas.width = CANVAS_PX
      canvas.height = CANVAS_PX
      ctx = canvas.getContext('2d')
      paths = buildPaths()
    } catch {
      ctx = null
    }
  }

  // Canvas rendering unavailable: the declared static icon is already correct.
  if (!ctx || paths.length === 0) {
    settle()
    return stop
  }

  if (prefersReduced?.matches) {
    // Rest on the static icon, but stay wired up: onReducedChange can start us later.
    settle()
    return stop
  }

  begin()

  return stop
}

export const ANIMATED_FAVICON_REVEAL_MS = REVEAL_MS
export const ANIMATED_FAVICON_FRAME_INTERVAL_MS = FRAME_INTERVAL_MS
export const ANIMATED_FAVICON_FRAME_COUNT = FRAME_COUNT
export const ANIMATED_FAVICON_PULSE_INTERVAL_MS = PULSE_INTERVAL_MS
export const ANIMATED_FAVICON_PULSES_PER_BURST = PULSES_PER_BURST
export const ANIMATED_FAVICON_BURST_REST_MS = BURST_REST_MS
